import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";

import { EntityRefPicker } from "@/components/fields/entity-ref-picker";
import { useVenueRepository } from "@/pages/venues/hooks/use-venue-repository";

interface VenueCard {
  id: string;
  name?: string | null;
  address?: string | null;
  media?: { photos?: { url?: string }[] } | null;
}

/**
 * A match's venue. The form holds only `venueId`; the card is looked up by id. There is
 * deliberately no clear button: picking a venue makes it a Curator venue that TINA never
 * overrides, and the backend PUT cannot clear a field.
 */
export function MatchVenueField({ id }: AutoFormFieldProps) {
  const { search, get } = useVenueRepository();
  const form = useFormContext();
  const venueId: string | null | undefined = useWatch({ name: id });
  const [venue, setVenue] = useState<VenueCard | null>(null);

  useEffect(() => {
    if (!venueId) {
      setVenue(null);
      return;
    }
    if (venue?.id === venueId) return;
    let cancelled = false;
    get(venueId)
      .then((v: any) => {
        if (!cancelled) setVenue({ ...v, id: venueId });
      })
      .catch(() => {
        if (!cancelled) setVenue({ id: venueId });
      });
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  const pick = (doc: any) => {
    if (!doc?.id || doc.id === venueId) return;
    setVenue(doc);
    form.setValue(id, doc.id, { shouldDirty: true });
  };

  return (
    <EntityRefPicker<any>
      current={
        venueId
          ? {
              name: venue?.name ?? venueId,
              subtitle: venue?.address ?? undefined,
              imageUrl: venue?.media?.photos?.[0]?.url,
              href: `/venues/edit/${venueId}`,
            }
          : null
      }
      emptyLabel="No venue"
      onSearch={search}
      accessorFn={(doc) => ({
        id: doc?.id ?? "",
        name: [doc?.name, doc?.address].filter(Boolean).join(" · "),
        iconUrl: doc?.media?.photos?.[0]?.url,
      })}
      onPick={pick}
    />
  );
}
